// Shared primitive geometry for Model Studio: the same unit-sized geometry
// factory backs both the live editor (editor-model.tsx, scaled/positioned
// per element in a live R3F scene) and buildModelGroup's GLB export bake,
// so editing and export stay the same renderer as primitive types grow
// (docs/3d-content-pipeline/roadmap.md 3.1 Phase 1).
import {
  BoxGeometry,
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  ExtrudeGeometry,
  Float32BufferAttribute,
  Shape,
  SphereGeometry,
} from 'three';

/** Every primitive type Model Studio can place. */
export const MODEL_SHAPES = ['box', 'sphere', 'cylinder', 'cone', 'wedge', 'extrude'] as const;
export type ModelShape = (typeof MODEL_SHAPES)[number];

/** A 2D footprint polygon, XZ-plane points in an element's own local space
 * (docs/3d-content-pipeline/roadmap.md 3.1 Phase 3). */
export type ModelProfile = [number, number][];

/** An L-shape footprint - the default when a new extrude element is added,
 * chosen because a plain rectangle would be indistinguishable from a box
 * (the whole point of extrusion is a non-rectangular footprint). */
export const DEFAULT_MODEL_PROFILE: ModelProfile = [
  [0, 0], [10, 0], [10, 4], [4, 4], [4, 10], [0, 10],
];

/**
 * A right-triangular-prism ramp: a unit cube (-0.5..0.5 on every axis, same
 * convention as the built-in geometries below) with its back-top edge
 * collapsed onto its back-bottom edge, so it ramps down from a full-height
 * front face to nothing at the back. Non-indexed (each triangle gets its own
 * unshared vertices) so `computeVertexNormals` produces flat per-face
 * shading at the ramp's edges instead of smoothing across them - the same
 * faceted look every other primitive here has via its own per-face normals.
 * Manifold and consistently outward-wound (verified by hand via the
 * right-hand rule per face), which three-bvh-csg's boolean ops require.
 */
function createWedgeGeometry(): BufferGeometry {
  // Corners: front face is the tall (z=-0.5) rectangle; the back (z=0.5)
  // edge sits on the floor, so the ramp slopes up from back to front.
  const A: [number, number, number] = [-0.5, -0.5, -0.5]; // front-bottom-left
  const B: [number, number, number] = [0.5, -0.5, -0.5]; // front-bottom-right
  const C: [number, number, number] = [0.5, 0.5, -0.5]; // front-top-right
  const D: [number, number, number] = [-0.5, 0.5, -0.5]; // front-top-left
  const E: [number, number, number] = [-0.5, -0.5, 0.5]; // back-bottom-left
  const F: [number, number, number] = [0.5, -0.5, 0.5]; // back-bottom-right

  // One UV corner per named vertex, per face it appears on - a cube-style
  // unwrap (each of the wedge's five faces maps its own local span to the
  // full 0..1 unit square, the same "every face gets its own span" approach
  // every built-in three.js primitive uses for ITS own per-face UVs) rather
  // than a single UV projected across the whole shape, which would stretch
  // unevenly across faces of very different sizes and angles.
  interface Vertex { p: [number, number, number]; uv: [number, number] }
  const triangles: Vertex[][] = [
    // front (full-height rectangle at z=-0.5)
    [{ p: A, uv: [0, 0] }, { p: D, uv: [0, 1] }, { p: C, uv: [1, 1] }],
    [{ p: A, uv: [0, 0] }, { p: C, uv: [1, 1] }, { p: B, uv: [1, 0] }],
    // bottom (x/z rectangle at y=-0.5)
    [{ p: A, uv: [0, 0] }, { p: B, uv: [1, 0] }, { p: F, uv: [1, 1] }],
    [{ p: A, uv: [0, 0] }, { p: F, uv: [1, 1] }, { p: E, uv: [0, 1] }],
    // left cap (triangle at x=-0.5)
    [{ p: A, uv: [0, 0] }, { p: E, uv: [1, 0] }, { p: D, uv: [0, 1] }],
    // right cap (triangle at x=0.5)
    [{ p: B, uv: [0, 0] }, { p: C, uv: [0, 1] }, { p: F, uv: [1, 0] }],
    // ramp (sloped quad from the front-top edge down to the back-bottom edge)
    [{ p: D, uv: [0, 0] }, { p: F, uv: [1, 1] }, { p: C, uv: [1, 0] }],
    [{ p: D, uv: [0, 0] }, { p: E, uv: [0, 1] }, { p: F, uv: [1, 1] }],
  ];

  const positions: number[] = [];
  const uvs: number[] = [];
  for (const tri of triangles) {
    for (const vertex of tri) {
      positions.push(...vertex.p);
      uvs.push(...vertex.uv);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geometry.computeVertexNormals();
  return geometry;
}

/** Shoelace-formula polygon area, used to catch a degenerate (zero-area)
 * profile - e.g. every point collapsed onto the same spot, reachable in the
 * editor by deleting a profile down to its 3-point minimum after "+ Point"
 * (which always appends [0,0]) leaves them all coincident. `ExtrudeGeometry`
 * silently produces a 0-vertex, invisible mesh for such a profile rather
 * than throwing, so this has to be caught explicitly. */
function polygonArea(profile: ModelProfile): number {
  let area = 0;
  for (let i = 0; i < profile.length; i++) {
    const [x1, z1] = profile[i]!;
    const [x2, z2] = profile[(i + 1) % profile.length]!;
    area += x1 * z2 - x2 * z1;
  }
  return Math.abs(area) / 2;
}

/**
 * A footprint polygon extruded upward into a solid - the "2D-profile
 * extrude" stretch goal (docs/3d-content-pipeline/roadmap.md 3.1 Phase 3).
 * `profile` points are first normalised to fit the same -0.5..0.5 unit
 * footprint every other shape here uses (so `w`/`d` scale it exactly like
 * a box's footprint), extruded to unit depth along the shape's own local Z,
 * then rotated so that extrusion axis becomes world Y (`h`, height) instead
 * of three.js's own default (extrusion along Z, profile in XY) - matching
 * this module's "profile is the footprint, h is how tall it stands" model
 * rather than three.js's "profile is a wall, depth is how far it juts out."
 *
 * The profile's normalised Z is written into the Shape's local Y as-is
 * (`nz`, not `-nz`) - `Shape`'s own 2D plane is X/Y, and mapping the
 * footprint's X/Z axes onto it straight (X->X, Z->Y) is what keeps the
 * SOURCE contour's winding order (and hence `ExtrudeGeometry`'s own correct
 * outward-facing normals/manifold winding, which three-bvh-csg's boolean
 * ops require) exactly as authored - negating one of the two in-plane axes
 * here would flip that winding instead. The extrusion axis is then rotated
 * onto world Y via `rotateX(Math.PI / 2)` (not `-Math.PI / 2`): a rotation
 * is a proper rigid transform (determinant +1), so unlike negating a
 * coordinate before triangulation, EITHER rotation direction preserves
 * correct winding - `Math.PI / 2` is the one that maps local Y back onto
 * world Z un-mirrored (`+Math.PI/2`: newY=-oldZ, newZ=oldY=nz; the other
 * direction gives newZ=-oldY=-nz, mirroring the footprint's Z axis against
 * what was authored). The extruded prism is symmetric top-to-bottom (same
 * cross-section at every height), so this rotation choice has no visible
 * effect on the height axis either way - only the footprint's own
 * un-mirrored X/Z correctness depends on picking this direction.
 */
function createExtrudeGeometry(profile: ModelProfile): BufferGeometry {
  const xs = profile.map(([x]) => x);
  const zs = profile.map(([, z]) => z);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minZ = Math.min(...zs);
  const maxZ = Math.max(...zs);
  const spanX = Math.max(1e-6, maxX - minX);
  const spanZ = Math.max(1e-6, maxZ - minZ);

  const shape2d = new Shape();
  profile.forEach(([x, z], i) => {
    const nx = (x - minX) / spanX - 0.5;
    const nz = (z - minZ) / spanZ - 0.5;
    if (i === 0) shape2d.moveTo(nx, nz);
    else shape2d.lineTo(nx, nz);
  });
  shape2d.closePath();

  const geometry = new ExtrudeGeometry(shape2d, { depth: 1, bevelEnabled: false, curveSegments: 1 });
  geometry.translate(0, 0, -0.5);
  geometry.rotateX(Math.PI / 2);
  return geometry;
}

/**
 * A unit-sized geometry (roughly -0.5..0.5 on every axis, matching
 * `BoxGeometry(1,1,1)`) for the given shape - callers scale by an element's
 * own `w`/`h`/`d` and position it so its authored bottom face sits at its
 * authored `y`, exactly as the box case already did before other shapes
 * existed. Non-uniform scaling turns the sphere/cylinder/cone into
 * ellipsoids/elliptical variants, which is intentional - it's how every
 * other primitive here already supports non-uniform w/h/d, and needs no
 * extra parametrisation of its own. `profile` is only read for `'extrude'`
 * (defaulting to `DEFAULT_MODEL_PROFILE` when omitted) and ignored by every
 * other shape.
 */
export function createUnitModelGeometry(shape: ModelShape, profile?: ModelProfile): BufferGeometry {
  switch (shape) {
    case 'sphere':
      return new SphereGeometry(0.5, 24, 20);
    case 'cylinder':
      return new CylinderGeometry(0.5, 0.5, 1, 24);
    case 'cone':
      return new ConeGeometry(0.5, 1, 24);
    case 'wedge':
      return createWedgeGeometry();
    case 'extrude':
      return createExtrudeGeometry(profile && profile.length >= 3 && polygonArea(profile) > 1e-6 ? profile : DEFAULT_MODEL_PROFILE);
    case 'box':
    default:
      return new BoxGeometry(1, 1, 1);
  }
}
